import React, { useState, createContext, useContext, useEffect } from 'react'
import { useCalendlyEventListener } from 'react-calendly'
import { apiGet, apiPatch, apiPost } from '../../util/api'
import _ from 'lodash'
import parse from 'html-react-parser'
import useServiceRequestForm from './ProfessionalServices/useServiceRequestForm'

const BenefitsContext = createContext()

export const useBenefits = () => {
  return useContext(BenefitsContext)
}

const BenefitsProvider = ({
  current_user,
  coach,
  service_requests,
  calendly_events_path,
  resume_review,
  resume_revisions,
  industries,
  revenue_options,
  company_sizes,
  service_request_class,
  children,
}) => {
  const [serviceRequestList, setServiceRequestList] = useState(service_requests)
  const [showScheduleDialog, setShowScheduleDialog] = useState(false)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [showReactFeedbackDialog, setShowReactFeedbackDialog] = useState(false)
  const [showCloseReviewDialog, setShowCloseReviewDialog] = useState(false)
  const toggleShowReactFeedbackDialog = () =>
    setShowReactFeedbackDialog(!showReactFeedbackDialog)
  const toggleShowFeedbackDialog = () =>
    setShowFeedbackDialog(!showFeedbackDialog)
  const [serviceRequestType, setServiceRequestType] = useState(
    service_request_class,
  )
  const [serviceRequest, setServiceRequest] = useState(
    serviceRequestList && serviceRequestList[service_request_class]
      ? serviceRequestList[service_request_class]
      : {},
  )
  const [feedbackContent, setFeedbackContent] = useState()
  const [showBackButton, setShowBackButton] = useState(true)
  const [dialogTitle, setDialogTitle] = useState('')
  const [meetingViaCalendly, setMeetingViaCalendly] = useState(false)
  const [candidateRevisionIndex, setCandidateRevisionIndex] = useState(0)
  const [resumeReview, setResumeReview] = useState(resume_review)
  const [page, setPage] = useState(1)
  const [uploadedResumeId, setUploadedResumeId] = useState(null)
  const [resumeRevisions, setResumeRevisions] = useState(resume_revisions)
  const { select } = current_user

  const toggleCloseReviewDialog = () =>
    setShowCloseReviewDialog(!showCloseReviewDialog)

  const serviceRequestDataMap = {
    'ServiceRequest::OrientationCall': '/orientation/call',
    'ServiceRequest::LinkedInReview':
      '/network-and-research/social_media_review',
    'ServiceRequest::ResumeReview': '/resume/review',
    'ResumeRevision::CandidateRevision': '/resume/revision',
  }

  const {
    serviceRequestForm,
    onSubmit,
    clearForm,
    submitted,
    handleBack,
    serviceRequestData,
  } = useServiceRequestForm({
    setPage,
    setShowBackButton,
    current_user,
    serviceRequestList,
    serviceRequestType,
  })

  const { watch, control, setValue } = serviceRequestForm

  const message = watch('message')
  const linkedinUrl = watch('linkedin_url')
  const seniorityLevel = watch('seniority_level')
  const documentId = watch('document_id')
  const resumeId = watch('resume_document_id')
  const notes = watch('notes')
  const remote_content = watch('remote_content')
  const industry_role = watch('industry_role')
  const location = watch('location')
  const industry = watch('industry')
  const publicOrPrivate = watch('public_or_private')
  const exampleTitle1 = watch('example_job_1')
  const exampleTitle2 = watch('example_job_2')
  const revenueRange = watch('revenue_range')
  const companySize = watch('company_size')
  const salary = watch('salary')

  const toggleShowScheduleDialog = () => {
    setShowScheduleDialog(!showScheduleDialog)
    clearForm()
  }

  useEffect(() => {
    if (uploadedResumeId !== null) {
      serviceRequestForm.setValue('resume_document_id', uploadedResumeId)
    }
  }, [serviceRequestForm, uploadedResumeId])

  useEffect(() => {
    if (serviceRequestList && serviceRequestList[serviceRequestType]) {
      setServiceRequest(serviceRequestList[serviceRequestType])
    }
  }, [serviceRequestList, serviceRequestType])

  const onScheduleClick = (
    type,
    cardTitle,
    meetingViaCalendly,
    page = 1,
    coachRevisionCount = 1,
  ) => {
    setServiceRequestType(type)
    setDialogTitle(cardTitle)
    setMeetingViaCalendly(meetingViaCalendly)
    setCandidateRevisionIndex(coachRevisionCount - 1)
    setPage(page)
    setShowScheduleDialog(true)
  }

  const onFeedbackClick = (type) => {
    const fetchFeedbackContent = async () => {
      try {
        const res = await apiGet(serviceRequestDataMap[type], {
          headers: { Accept: 'text/html' },
        })
        if (res.status === 200) {
          setFeedbackContent(parse(res.data))
          setShowFeedbackDialog(true)
        }
      } catch (error) {
        console.error('Error fetching feedback content:', error)
      }
    }
    fetchFeedbackContent()
  }

  const onCancelClick = () => {
    let intervalId

    const fetchCalendlyEvents = async () => {
      try {
        const res = await apiGet('/coach_services/check_calendly_events')
        if (res.status === 200) {
          const newServiceRequestList = res.data.service_requests
          const newResumeRevisions = res.data.resume_revisions
          if (
            serviceRequestList &&
            !_.isEqual(JSON.parse(newServiceRequestList), serviceRequestList)
          ) {
            setServiceRequestList(JSON.parse(newServiceRequestList))
            clearInterval(intervalId)
          }
          if (
            resumeRevisions &&
            !_.isEqual(JSON.parse(newResumeRevisions), resumeRevisions)
          ) {
            setResumeRevisions(JSON.parse(newResumeRevisions))
            clearInterval(intervalId)
          }
        }
      } catch (error) {
        console.error('Error fetching Calendly events:', error)
      }
    }

    intervalId = setInterval(fetchCalendlyEvents, 20000)

    return () => clearInterval(intervalId)
  }

  const onDeclineRevisionClilck = async (requestId) => {
    try {
      const res = await apiPost('/coach_services/decline_revision', {
        id: requestId,
      })
      if (res.status === 200) {
        setResumeReview(res.data.resume_review)
        toggleCloseReviewDialog()
      }
    } catch (error) {
      console.error('Error fetching Calendly events:', error)
    }
  }

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      const payload = e.data.payload
      setShowBackButton(false)
      createCalendlyEvent(
        payload.event.uri.split('/').pop(),
        payload.invitee.uri.split('/').pop(),
      )
    },
  })

  const changeResume = async (serviceRequestId) => {
    const data = {
      service_request: { document_id: uploadedResumeId },
    }
    const res = await apiPatch(
      `/coach_services/update_resume_document/${serviceRequestId}`,
      data,
    )

    if (res.status === 200) {
      setTimeout(() => {
        setResumeReview(res.data.resume_review)
        toggleShowScheduleDialog()
      }, 2000)
      return res
    } else {
      throw new Error('Service request creation failed')
    }
  }

  const createServiceRequest = async () => {
    const res = await apiPost('/coach_services/create_service_request', {
      service_request: {
        ...serviceRequestData,
        meeting_via_calendly:
          serviceRequestType == 'ServiceRequest::ResumeReview'
            ? meetingViaCalendly
            : false,
        type: serviceRequestType,
      },
    })

    if (res.status === 200) {
      setTimeout(() => {
        setServiceRequestList((prev) => ({
          ...prev,
          [serviceRequestType]: {
            meeting_via_calendly: false,
            completed: false,
          },
        }))
        if (serviceRequestType === 'ServiceRequest::ResumeReview') {
          setResumeReview(res.data.service_request)
        }
        toggleShowScheduleDialog()
      }, 2000)
      return res
    } else {
      throw new Error('Service request creation failed')
    }
  }

  const createRevisionRequest = async () => {
    const revisionRequestPayload = {
      ...serviceRequestData,
    }

    const res = await apiPost(
      '/coach_services/create_revision_request',
      {
        service_request: revisionRequestPayload,
      },
      true,
    )

    if (res.status === 200) {
      setTimeout(() => {
        setResumeReview(res.data.resume_review)
        setResumeRevisions(res.data.resume_revisions)
        toggleShowScheduleDialog()
      }, 2000)
      return res
    } else {
      throw new Error('Service request creation failed')
    }
  }

  const createCalendlyEvent = async (calendlyId, calendlyUserId) => {
    const data = {
      request_format: 'json',
      calendly_event: {
        candidate_id: current_user.candidate_id,
        coach_id: coach.id,
        calendly_id: calendlyId,
        calendly_user_id: calendlyUserId,
        service_request_type: serviceRequestType,
        service_request_data: {
          ...serviceRequestData,
          candidate_revision_index: candidateRevisionIndex,
        },
        ...(resumeReview?.id && { service_request_id: resumeReview.id }),
      },
    }

    const res = await apiPost(calendly_events_path, data, true)

    if (res.status === 200) {
      setTimeout(() => {
        const event = res.data.calendlyEvent
        const revisions = res.data.resumeRevisions
        const review = res.data.resumeReview

        if (revisions?.length > 0) {
          setResumeRevisions(revisions)
          setResumeReview(review)
        } else {
          setServiceRequestList((prev) => ({
            ...prev,
            [serviceRequestType]: { calendly_event: event, completed: false },
          }))
        }
        toggleShowScheduleDialog()
      }, 2000)
      return res
    } else {
      throw new Error('Calendly event creation failed')
    }
  }

  const values = {
    current_user,
    coach,
    showScheduleDialog,
    toggleShowScheduleDialog,
    showFeedbackDialog,
    toggleShowFeedbackDialog,
    showReactFeedbackDialog,
    toggleShowReactFeedbackDialog,
    setShowReactFeedbackDialog,
    serviceRequestType,
    setServiceRequestType,
    onScheduleClick,
    onCancelClick,
    onFeedbackClick,
    onDeclineRevisionClilck,
    serviceRequestForm,
    serviceRequestList,
    feedbackContent,
    showBackButton,
    dialogTitle,
    message,
    linkedinUrl,
    createServiceRequest,
    meetingViaCalendly,
    seniorityLevel,
    resumeId,
    documentId,
    uploadedResumeId,
    setUploadedResumeId,
    resumeReview,
    setResumeReview,
    page,
    handleBack,
    control,
    onSubmit,
    submitted,
    resumeRevisions,
    createRevisionRequest,
    notes,
    remote_content,
    select,
    industries,
    industry_role,
    location,
    revenue_options,
    company_sizes,
    industry,
    publicOrPrivate,
    exampleTitle1,
    exampleTitle2,
    revenueRange,
    companySize,
    salary,
    setDialogTitle,
    setPage,
    setShowScheduleDialog,
    setValue,
    showCloseReviewDialog,
    setShowCloseReviewDialog,
    toggleCloseReviewDialog,
    changeResume,
    serviceRequest,
    setServiceRequest,
  }
  return (
    <BenefitsContext.Provider value={values}>
      {children}
    </BenefitsContext.Provider>
  )
}
export default BenefitsProvider
